import {
  ArrowDownIcon,
  Bars3Icon,
  PencilSquareIcon,
} from "@heroicons/react/16/solid";
import { ChatMessageDTO } from "@onetutor/shared/dto";
import { MessageEnum } from "@onetutor/shared/type";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router";
import { v4 as uuidv4 } from "uuid";
import Header from "../../components/header/Header";
import Modal, { ModalVariant } from "../../components/modal";
import { useToastEffect } from "../../hooks";
import { receiveStream } from "../../services/chatStreaming";
import {
  useDeleteChatSessionMutation,
  useGetChatHistoryQuery,
  useGetChatSessionsQuery,
  useGetCourseQuery,
  usePatchChatSessionMutation,
  usePostNewChatMutation,
} from "../../services/courseApi";
import { ChatBubble, ChatBubbleStream } from "./components/ChatBubble";
import { ChatInputBar } from "./components/ChatInputBar";
import { ChatSessionOverview } from "./components/ChatSessionOverview";
import {
  TypewriterLoading,
  TypewriterWelcome,
} from "./components/TypewriterComps";

const ChatPage: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessageDTO[]>([]);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const [chatSessionToDelete, setChatSessionToDelete] = useState<string>("");
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [citations, setCitations] = useState<string[]>([]);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);

  const drawerToggleRef = useRef<HTMLInputElement>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get("sessionId");

  const deleteChatSessionModalId = "deleteChatSessionModal";

  const { t } = useTranslation(["chatPage", "toast", "header", "commons"]);

  const { courseId } = useParams<{
    courseId: string;
  }>();

  const { data: course } = useGetCourseQuery(courseId || "");

  const [isStreaming, setIsStreaming] = useState(false);
  const [isWaitingForFirstToken, setIsWaitingForFirstToken] = useState(false);
  const [lastMessage, setLastMessage] = useState<string>("");
  const messagesRef = useRef<string[]>([]);
  const isFirstRender = useRef(true);

  const {
    data: chatSessions,
    isError: chatSessionsIsError,
    error: chatSessionsError,
  } = useGetChatSessionsQuery(courseId || "");

  const currentChatSession = chatSessions?.find(
    (session) => session.id === sessionId,
  );

  const [
    patchChatSession,
    { error: patchChatSessionError, isError: patchChatSessionIsError },
  ] = usePatchChatSessionMutation();

  const [
    deleteChatSession,
    { error: deletChatSessionError, isError: deletChatSessionIsError },
  ] = useDeleteChatSessionMutation();

  useToastEffect({
    isError: deletChatSessionIsError,
    error: deletChatSessionError,
  });

  useToastEffect({
    isError: chatSessionsIsError,
    error: chatSessionsError,
  });

  useToastEffect({
    isError: patchChatSessionIsError,
    error: patchChatSessionError,
  });

  const {
    data: chatHistory,
    isError: chatHistoryIsError,
    error: chatHistoryError,
    refetch: refetchChatHistory,
    isFetching: isFetchingChatHistory,
  } = useGetChatHistoryQuery(
    {
      courseId: courseId ?? "",
      sessionId: sessionId ?? "",
    },
    {
      skip: isStreaming,
    },
  );

  useToastEffect({ isError: chatHistoryIsError, error: chatHistoryError });

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    setShowScrollButton(false);
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!isStreaming) {
      refetchChatHistory();
    }
  }, [isStreaming, refetchChatHistory]);

  useLayoutEffect(() => {
    if (sessionId !== "" && !isStreaming) {
      setMessages([...(chatHistory?.chatMessages ?? [])]);
      scrollToBottom();
    }
  }, [chatHistory, sessionId, isStreaming, scrollToBottom]);

  useLayoutEffect(() => {
    if (shouldScrollToBottom) {
      scrollToBottom();
      setShouldScrollToBottom(false);
    }
  }, [shouldScrollToBottom, scrollToBottom]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowScrollButton(!entry.isIntersecting);
      },
      { threshold: 0 },
    );

    if (messagesEndRef.current) {
      observer.observe(messagesEndRef.current);
    }

    return () => {
      if (messagesEndRef.current) {
        observer.unobserve(messagesEndRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (lastMessage) {
      scrollToBottom();
    }
  }, [lastMessage, scrollToBottom]);

  const firstHumanMessage =
    messages.length > 0
      ? messages.find((message) => message.author === MessageEnum.HUMAN)
      : null;

  const nameChatSession = useCallback(() => {
    if (
      firstHumanMessage &&
      sessionId &&
      courseId &&
      !currentChatSession?.name
    ) {
      const content = firstHumanMessage.content.substring(0, 30) || "";
      patchChatSession({
        sessionId: sessionId,
        courseId: courseId,
        body: {
          name: content,
        },
      });
    }
  }, [
    firstHumanMessage,
    sessionId,
    courseId,
    currentChatSession?.name,
    patchChatSession,
  ]);

  const startChatStream = async (sessionIdTemp: string, input: string) => {
    messagesRef.current = [];

    if (courseId && sessionIdTemp) {
      receiveStream({
        courseId,
        sessionId: sessionIdTemp,
        setLastMessage,
        setIsStreaming,
        setMessages,
        setIsWaitingForFirstToken,
        userInput: input,
        setCitations,
        citationsEnabled: !!course?.citationsEnabled,
      });
    }
  };

  useEffect(() => {
    nameChatSession();
  }, [nameChatSession]);

  const [newChat, { isError: newChatIsError, error: newChatError }] =
    usePostNewChatMutation();

  useToastEffect({ isError: newChatIsError, error: newChatError });

  const newChatPressed = async () => {
    setMessages([]);
    searchParams.delete("sessionId");
    setSearchParams(searchParams, { replace: true });
    if (drawerToggleRef.current) {
      drawerToggleRef.current.checked = false;
    }
  };

  const handleSubmit = async (input: string) => {
    if (isStreaming) {
      return;
    }
    setIsStreaming(true);

    let sessionIdTemp = sessionId;

    if (!sessionIdTemp && courseId) {
      const chatResponse = await newChat({ courseId });

      if (chatResponse.data) {
        setSearchParams({ sessionId: chatResponse.data.id }, { replace: true });
        sessionIdTemp = chatResponse.data.id;
      }
    }

    const newMessage = {
      id: uuidv4(),
      content: input,
      author: MessageEnum.HUMAN,
      citations: null,
    };

    setMessages([...messages, newMessage]);
    setLastMessage("");
    setShouldScrollToBottom(true);

    if (input && sessionIdTemp && courseId) {
      setIsWaitingForFirstToken(true);
      startChatStream(sessionIdTemp, input);
    }
  };

  return (
    <Header
      scrollable={false}
      bottomPadding={false}
      breadcrumbs={[
        {
          name:
            course?.name ?? t("breadcrumbFallback.course", { ns: "header" }),
          link: `/course/${courseId}`,
        },
        { name: t("chat") },
      ]}
    >
      <div className="drawer drawer-start h-full">
        <input
          id="session-drawer"
          ref={drawerToggleRef}
          type="checkbox"
          className="drawer-toggle"
        />
        <div className="drawer-content z-0 flex flex-col overflow-hidden">
          <div className="flex flex-none items-center">
            <label
              htmlFor="session-drawer"
              className="btn btn-square btn-sm btn-primary drawer-button"
            >
              <Bars3Icon className="h-6 w-6" />
            </label>
            {currentChatSession && (
              <div className="ml-md text-center">
                <h2 className="font-bold text-lg">{currentChatSession.name}</h2>
              </div>
            )}
          </div>
          <div className="flex grow justify-center overflow-hidden">
            <div className="flex w-full max-w-5xl rounded-2xl">
              <div className="z-0 flex w-full flex-col py-2 md:px-2">
                <div
                  id="chatContainer"
                  className="mb-2 grow overflow-y-auto rounded-box"
                >
                  {messages.map((message) => (
                    <ChatBubble
                      key={message.id}
                      message={message}
                      messageArray={messages}
                      citations={citations}
                    />
                  ))}
                  {isWaitingForFirstToken && <TypewriterLoading />}
                  {lastMessage.length > 0 && (
                    <ChatBubbleStream
                      lastMessage={lastMessage}
                      isStreaming={isStreaming}
                      setCitations={setCitations}
                    />
                  )}
                  <div ref={messagesEndRef} />
                </div>
                {messages.length === 0 && !isWaitingForFirstToken && (
                  <TypewriterWelcome />
                )}
                <div className="relative">
                  {showScrollButton && (
                    <button
                      type="button"
                      onClick={scrollToBottom}
                      className="btn btn-circle -top-20 absolute inset-x-0 mx-auto bg-primary-neutral opacity-75"
                    >
                      <ArrowDownIcon className="h-6 w-6" />
                    </button>
                  )}
                  <ChatInputBar
                    handleSubmit={handleSubmit}
                    isStreaming={isStreaming}
                    isFetchingChatHistory={isFetchingChatHistory}
                    newChatPressed={newChatPressed}
                    messages={messages}
                  />
                </div>
                <div className="mt-sm hidden justify-center whitespace-pre-line text-center text-xs opacity-50 sm:block sm:text-sm">
                  {t("disclaimer")}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="drawer-side z-50">
          <label
            htmlFor="session-drawer"
            aria-label="close sidebar"
            className="drawer-overlay"
          />
          <ul className="flex h-full w-80 flex-col bg-neutral p-4 text-base-content md:w-100">
            <li className="mb-2 flex flex-none items-center justify-between">
              <span className="font-bold text-base-content text-lg md:text-xl">
                {t("chatSession.overviewTitle", { ns: "chatPage" })}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={newChatPressed}
              >
                {t("startNewChat")} <PencilSquareIcon className="h-5 w-5" />
              </button>
            </li>
            <li className="w-full grow">
              <div className="menu w-full">
                {chatSessions && chatSessions.length > 0 ? (
                  <ChatSessionOverview
                    chatSessions={chatSessions}
                    setSearchParams={setSearchParams}
                    currentChatSession={currentChatSession}
                    setChatSessionToDelete={setChatSessionToDelete}
                    patchChatSession={patchChatSession}
                    deleteChatSessionModalId={deleteChatSessionModalId}
                  />
                ) : (
                  t("chatSession.noChats", { ns: "chatPage" })
                )}
              </div>
            </li>
          </ul>
        </div>
      </div>
      <Modal
        id={deleteChatSessionModalId}
        title={t("chatSession.deleteModalTitle", { ns: "chatPage" })}
        content={t("chatSession.deleteModalContent", { ns: "chatPage" })}
        onConfirm={() => {
          if (courseId && chatSessionToDelete) {
            deleteChatSession({
              sessionId: chatSessionToDelete,
              courseId: courseId,
            });
            if (sessionId === chatSessionToDelete) {
              setMessages([]);
              setSearchParams({ sessionId: "" }, { replace: true });
            }
          }
        }}
        confirmVariant={ModalVariant.DESTRUCTIVE}
        onCancel={() => {}}
      />
    </Header>
  );
};

export default ChatPage;
